import { useEffect, useState } from "react";
import { Globe, MonitorPlay, PowerOff, Siren } from "lucide-react";
import type { ManifestPayload } from "@/lib/types";
import { BANNER_FONTS, DEFAULT_BANNER_STYLE } from "@/lib/format";
import { BANNER_SIZES, BANNER_SPEEDS, bannerBg } from "@/components/app/BannerStyleEditor";
import { AnalogClock, ClockSlide, RssSlide, WeatherSlide, formatClock, useNow } from "@/components/app/MediaSlides";

type Cfg = ManifestPayload["config"];
type Media = Cfg["media"][number];

function Slide({ media }: { media: Media }) {
  if (media.type === "image") return <img src={media.url} alt={media.name} className="h-full w-full object-contain" />;
  if (media.type === "video") return <video src={media.url} autoPlay muted loop className="h-full w-full object-contain" />;
  if (media.type === "text") return <div className="flex h-full items-center justify-center p-6 text-center font-display text-xl font-semibold text-white md:text-3xl">{media.url}</div>;
  if (media.type === "weather") return <WeatherSlide url={media.url} options={media.options || {}} />;
  if (media.type === "clock") return <ClockSlide options={media.options || {}} />;
  if (media.type === "rss") return <RssSlide url={media.url} />;
  return <div className="flex h-full flex-col items-center justify-center gap-2 text-slate-300"><Globe className="h-10 w-10 text-cyan-400" /><p className="text-sm">Page Web</p><p className="max-w-[80%] truncate font-mono text-xs text-slate-500">{media.url}</p></div>;
}

function ListPlayer({ cfg, listId, testId }: { cfg: Cfg; listId: string | null; testId: string }) {
  const list = cfg.lists.find((l) => l.id === listId);
  const [idx, setIdx] = useState(0);
  const item = list?.items[idx % (list?.items.length || 1)];
  const media = cfg.media.find((m) => m.id === item?.id);
  const tr = cfg.transition || { type: "none", duration_ms: 800 };
  const type = item?.transition || tr.type;
  useEffect(() => {
    if (!item) return;
    const t = setTimeout(() => setIdx((i) => i + 1), Math.min(item.seconds, 8) * 1000);
    return () => clearTimeout(t);
  }, [item, idx]);
  if (!media) return <div className="flex h-full flex-col items-center justify-center gap-2 text-slate-500"><MonitorPlay className="h-8 w-8" /><p className="text-xs">Aucun contenu assigné</p></div>;
  return (
    <div key={idx} data-testid={testId} data-transition={type} className={`h-full w-full ${type !== "none" ? `tr-${type}` : ""}`} style={{ animationDuration: `${tr.duration_ms}ms` }}>
      <Slide media={media} />
    </div>
  );
}

function WallCrop({ cfg }: { cfg: Cfg }) {
  const w = cfg.video_wall!;
  return (
    <div className="relative h-full w-full overflow-hidden" data-testid="tv-preview-wall">
      <div className="absolute" style={{ width: `${w.cols * 100}%`, height: `${w.rows * 100}%`, left: `-${w.col * 100}%`, top: `-${w.row * 100}%` }}>
        <ListPlayer cfg={cfg} listId={w.list} testId="tv-preview-slide" />
      </div>
      <span className="absolute left-3 top-3 z-10 rounded bg-orange-500/90 px-2 py-0.5 font-mono text-[10px] font-semibold text-white">Mur « {w.name} » · colonne {w.col + 1}, rangée {w.row + 1}</span>
    </div>
  );
}

function Overlays({ cfg }: { cfg: Cfg }) {
  const now = useNow();
  const style = cfg.banner_style || (DEFAULT_BANNER_STYLE as any);
  const bannerTop = style.position === "top";
  return (
    <>
      {cfg.clock && (
        <div data-testid="tv-preview-clock" className={`absolute right-4 ${bannerTop ? "bottom-4" : "top-4"} z-10 rounded-md bg-black/60 px-3 py-1 font-mono text-lg font-semibold text-white backdrop-blur`}>
          {cfg.clock_style === "analog" ? <AnalogClock date={now} size={56} /> : formatClock(now, cfg.clock_style || "digital_24h")}
        </div>
      )}
      {(cfg.banner || style.rss_url) && (
        <div className={`absolute inset-x-0 ${bannerTop ? "top-0" : "bottom-0"} z-10 overflow-hidden py-2`} style={{ background: bannerBg(style) }}>
          <p data-testid="tv-preview-banner" className={`animate-marquee whitespace-nowrap font-medium ${BANNER_SIZES[style.size]}`}
            style={{ fontFamily: BANNER_FONTS[style.font]?.css, color: style.color, animationDuration: `${BANNER_SPEEDS[style.speed]}s` }}>
            {cfg.banner}{style.rss_url ? `   •   [Titres RSS : ${style.rss_url}]` : ""}
          </p>
        </div>
      )}
    </>
  );
}

export function TvPreview({ manifest, activeList, powerOn = true }: { manifest: ManifestPayload; activeList: string | null; powerOn?: boolean }) {
  const cfg = manifest.config;
  const portrait = cfg.orientation === "portrait" || cfg.orientation === "portrait_reverse";
  const zones = cfg.layout?.zones || [{ id: "main", x: 0, y: 0, w: 1, h: 1, list: null }];
  const mainList = activeList || cfg.defaultId;

  return (
    <div data-testid="tv-preview" data-orientation={cfg.orientation || "landscape"} data-layout={cfg.layout?.type || "full"} className={`relative mx-auto overflow-hidden rounded-xl border-[6px] border-slate-800 shadow-2xl shadow-black/60 ${portrait ? "aspect-[9/16] w-full max-w-[340px]" : "aspect-video w-full"}`} style={{ background: cfg.bg }}>
      {!powerOn && <div data-testid="tv-preview-power-off" className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-2 bg-black text-slate-500"><PowerOff className="h-8 w-8" /><p className="text-sm">Écran éteint selon l'horaire d'allumage</p></div>}
      {cfg.alert && (
        <div data-testid="tv-preview-alert" className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 p-8 text-center" style={{ background: cfg.alert.bg, color: cfg.alert.color }}>
          <Siren className="h-10 w-10 animate-pulse" /><p className="font-display text-2xl font-bold md:text-4xl">{cfg.alert.message}</p>
        </div>
      )}
      {cfg.video_wall ? <WallCrop cfg={cfg} /> : zones.map((z) => (
        <div key={z.id} data-testid={`tv-preview-zone-${z.id}`} className="absolute overflow-hidden border-slate-950/60" style={{ left: `${z.x * 100}%`, top: `${z.y * 100}%`, width: `${z.w * 100}%`, height: `${z.h * 100}%`, borderWidth: zones.length > 1 ? 1 : 0, borderColor: zones.length > 1 ? "#334155" : undefined }}>
          <ListPlayer cfg={cfg} listId={z.id === "main" ? mainList : z.list} testId={z.id === "main" ? "tv-preview-slide" : `tv-preview-zone-${z.id}-slide`} />
        </div>
      ))}
      <Overlays cfg={cfg} />
    </div>
  );
}
